/**
 * Operations-team knowledge base. Powers the <InfoPopover> next to every
 * <SectionHead> across the admin portal so an ops teammate can refresh
 * themselves on what a feature does without leaving the page.
 *
 * Authoring rules:
 *   - UK English ("favourite", "centre", "organisation").
 *   - Keep summary to 1–3 sentences; lean on `features[]` for detail.
 *   - Lead with what the operations team needs — not internal architecture.
 */

export interface KnowledgeBaseFeature {
  name: string;
  description: string;
}

export interface KnowledgeBaseEntry {
  title: string;
  summary: string;
  features?: KnowledgeBaseFeature[];
}

export type KnowledgeBaseKey =
  | 'dashboard.recent-records'
  | 'logistics.orders'
  | 'logistics.route-plan'
  | 'logistics.warehouse'
  | 'logistics.forecast'
  | 'partners.list'
  | 'partners.access'
  | 'partners.pricing'
  | 'partners.vehicles'
  | 'partners.service-areas'
  | 'customers.list'
  | 'products.list'
  | 'discounts.list'
  | 'invoices.list'
  | 'admins.list';

export const knowledgeBase: Record<KnowledgeBaseKey, KnowledgeBaseEntry> = {
  'dashboard.recent-records': {
    title: 'Recent records',
    summary:
      'The most recently created orders, removals enquiries and quote requests across all partners. Selecting a row opens the full record.',
    features: [
      {
        name: 'Quote rows',
        description:
          'Pending quotes that have not yet been converted to an order appear in the same list so they do not get lost.',
      },
      {
        name: 'Partner badge',
        description:
          'For storage orders, the Partners column summarises whether the collection courier, delivery courier and warehouse have all been assigned.',
      },
    ],
  },

  'logistics.orders': {
    title: 'Logistics orders',
    summary:
      'All storage orders for the selected partner together with their collection and delivery scheduling. Use the filters to narrow by status, date or driver assignment.',
    features: [
      {
        name: 'ETA windows',
        description:
          'Each order can carry an explicit collection and delivery time window. The window persists per order; if not set, it is derived from the booked timeslot.',
      },
      {
        name: 'Sub-order splits',
        description:
          'A single parent order can be split into sub-orders (C1 / C2 …) so different boxes go to different couriers on different days. Splits clear the parent\'s driver assignments and label boxes as "{parentId}/{code}".',
      },
      {
        name: 'Capability gating',
        description:
          'Splits, unsplits, route overrides and day-locks require the canControlLogistics capability — Master users have it by default; drivers and warehouse staff do not.',
      },
    ],
  },

  'logistics.route-plan': {
    title: 'Route plan',
    summary:
      'Daily route view per driver. Stops are auto-sequenced from the partner\'s service area and time windows; admins can drag-to-reorder.',
    features: [
      {
        name: 'Day-locks',
        description:
          'Once a driver starts their day, the plan can be locked so subsequent admin changes do not silently overwrite a live route. Unlocking requires admin or master.',
      },
      {
        name: 'Manual overrides',
        description:
          'Reordered stops persist per driver per day, so the next morning starts from a clean auto-plan unless the override is re-applied.',
      },
    ],
  },

  'logistics.warehouse': {
    title: 'Warehouse dashboard',
    summary:
      'Inbound (boxes arriving from collection couriers), Outbound (boxes leaving for delivery), Storage (currently stored) and Box history (every scan event). Use the View control to switch.',
    features: [
      {
        name: 'Box status flow',
        description:
          'WCAC → ITKK → WKKU → WKKS → WKKDO → WKKP → WKKHO → ITTC → WC. Failed paths: WCFC (failed collection), ITTKKFR (failed delivery return). Each warehouse scan advances the status; the backend validates every transition.',
      },
      {
        name: 'Issue flagging',
        description:
          'Damaged, overweight, repack and other issues are recorded by warehouse staff during scan. They appear in the Issue filter and the CSV export.',
      },
    ],
  },

  'logistics.forecast': {
    title: 'Capacity forecast',
    summary:
      'Forward-looking view of inbound and outbound volume by day. Used to plan warehouse staffing and partner courier capacity ahead of busy weeks.',
  },

  'partners.list': {
    title: 'Partners',
    summary:
      'Every onboarded courier and warehouse partner. Each partner has its own pricing, vehicles, service areas and user access.',
    features: [
      {
        name: 'Master role',
        description:
          'A partner Master is the singularity between courier and warehouse for one partner — they see both flows on the scanner and can promote drivers and warehouse staff to capability holders.',
      },
      {
        name: 'Internal team policy',
        description:
          'Every internal team member is provisioned with two accounts: an Admin login on the booking portal and a Master login on the scanner.',
      },
    ],
  },

  'partners.access': {
    title: 'Partner user access',
    summary:
      'All partner users across all partners with their role (Master / Driver / Warehouse) and the canControlLogistics capability flag.',
    features: [
      {
        name: 'Capability flag',
        description:
          'canControlLogistics gates splits, unsplits, route overrides and day-locks. Master defaults on; drivers and warehouse staff default off.',
      },
    ],
  },

  'partners.pricing': {
    title: 'Partner pricing',
    summary:
      'Per-partner courier and warehouse rate cards. Drives invoice generation and the cost-per-order shown on order detail pages.',
  },

  'partners.vehicles': {
    title: 'Partner vehicles',
    summary:
      'The vehicle fleet registered against this partner — capacity per vehicle informs route planning.',
  },

  'partners.service-areas': {
    title: 'Partner service areas',
    summary:
      'Postcode prefixes this partner covers for collection and delivery. Orders outside the area are flagged for manual review.',
  },

  'customers.list': {
    title: 'Customers',
    summary:
      'Every registered customer. Selecting a row opens the profile, where you can review orders, update contact details and resolve account questions.',
  },

  'products.list': {
    title: 'Products',
    summary:
      'Storage boxes, suitcases, tape and accessories. Pricing by box type and subscription tier flows from here through to the booking flow.',
  },

  'discounts.list': {
    title: 'Discounts',
    summary:
      'Promo codes available at checkout. Each discount has an active window and an optional usage limit.',
  },

  'invoices.list': {
    title: 'Invoices',
    summary:
      'Generated invoices for partner storage and labour. Filter by partner and month to reconcile what was booked against what was paid.',
  },

  'admins.list': {
    title: 'Admins',
    summary:
      'Internal team accounts with admin or warehouse-supervisor access to this portal. Per operating policy, every internal teammate also has a Master account on the scanner app.',
  },
};
